import { useQuery } from '@tanstack/react-query';
import { Radio } from 'antd';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Button, Input, Select } from '../../../custom';
import { useCreateUpdateRequiredDocumentMutation } from '../../../hooks/api/useCreateUpdateRequiredDocumentMutation';

import { getProgramTypes } from '../programType/request';

interface RequiredDocumentFormProps {
  handleClose: () => void;
  record?: RequiredDocument | null;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Document name is required'),
  maxFileSizeInMB: Yup.number()
    .typeError('Max file size is required')
    .min(0, 'Max file size cannot be negative')
    .required('Max file size is required'),
  allowedFileTypes: Yup.string().required('Allowed file types are required'),
  isRequired: Yup.string().required('Required status is required'),
  programType: Yup.string().required('Program type is required'),
  description: Yup.string(),
  hasTextField: Yup.string().required('This field is required'),
  isTextFieldRequired: Yup.string().when('hasTextField', {
    is: 'true',
    then: (schema) => schema.required('Please specify if the certificate/document number is required'),
  }),
});

const requiredOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const booleanRadioOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const RequiredDocumentForm = ({ handleClose, record }: RequiredDocumentFormProps) => {
  const { data: programTypeResponse, isLoading: isProgramTypesLoading } = useQuery({
    queryKey: ['get-program-types'],
    queryFn: getProgramTypes,
  });

  const { createUpdateRequiredDocumentHandler, isLoading } = useCreateUpdateRequiredDocumentMutation(handleClose);

  const programTypes = programTypeResponse?.data ?? [];

  const programTypeOptions = programTypes?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  return (
    <Formik
      initialValues={{
        id: record?.id ?? 0,
        name: record?.name ?? '',
        maxFileSizeInMB: record?.maxFileSizeInMB ?? '',
        allowedFileTypes: record?.allowedFileTypes ?? '',
        isRequired: String(record?.isRequired ?? ''),
        programType: String(record?.programTypeId ?? ''),
        description: record?.description ?? '',
        hasTextField: String(record?.hasTextField ?? false),
        isTextFieldRequired: String(record?.isTextFieldRequired ?? false),
      }}
      onSubmit={(values, { resetForm }) => {
        createUpdateRequiredDocumentHandler(values, resetForm);
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue }) => (
        <Form className="fields">
          <Input name="name" label="Document Name" placeholder="Enter document name" />

          <Input
            name="maxFileSizeInMB"
            label="Max File Size (MB)"
            placeholder="Enter max file size"
            type="number"
            min={0}
          />

          <Input name="allowedFileTypes" label="Allowed File Types" placeholder="e.g. pdf,jpg,png" />

          <Select
            key={isProgramTypesLoading ? 'program-type-loading' : 'program-type-loaded'}
            name="programType"
            label="Program Type"
            placeholder="Select program type"
            options={programTypeOptions}
            loading={isProgramTypesLoading}
          />

          <Select
            name="isRequired"
            label="Is Required"
            placeholder="Select required status"
            options={requiredOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          />

          <Input name="description" label="Description" placeholder="Enter description" type="textarea" />

          <Field name="hasTextField">
            {({ field, meta }: FieldProps) => (
              <div>
                <label>Requires Certificate/Document Number</label>
                <div>
                  <Radio.Group
                    {...field}
                    options={booleanRadioOptions}
                    onChange={(e) => {
                      setFieldValue('hasTextField', e.target.value);

                      if (e.target.value !== 'true') {
                        setFieldValue('isTextFieldRequired', 'false');
                      }
                    }}
                  />
                </div>
                {meta.touched && meta.error && <div className="error">{meta.error}</div>}
              </div>
            )}
          </Field>

          {values.hasTextField === 'true' && (
            <Field name="isTextFieldRequired">
              {({ field, meta }: FieldProps) => (
                <div>
                  <label>Is Certificate/Document Number Required</label>
                  <div>
                    <Radio.Group
                      {...field}
                      options={booleanRadioOptions}
                      onChange={(e) => setFieldValue('isTextFieldRequired', e.target.value)}
                    />
                  </div>
                  {meta.touched && meta.error && <div className="error">{meta.error}</div>}
                </div>
              )}
            </Field>
          )}

          <div className="btn-group">
            <Button type="button" onClick={handleClose} variant="text" text="Cancel" />
            <Button
              text={isLoading ? 'Submitting...' : record?.id ? 'Update' : 'Submit'}
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default RequiredDocumentForm;
